import { useState, useEffect, useCallback, useRef } from 'react';
import StatusPill from './StatusPill.jsx';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Reusable SharePoint Intake connect card.
 *
 * One self-contained UI that handles the entire happy path:
 *   1. No connection yet  → "Connect SharePoint" CTA + admin email field
 *   2. Invited            → "Email sent" + resend / change-email controls
 *   3. Consented          → "Pick a folder" → opens folder picker modal
 *   4. Active             → green status + folder info + manage controls
 *   5. Error / paused     → clear messaging + recovery action
 *
 * Used in two places:
 *   - ProvisionOrgWizard post-provision step (Vanta-style "next: connect")
 *   - Standalone SharePointIntakePage (permanent admin page)
 *
 * Props:
 *   orgId           — required
 *   orgName         — for display
 *   inviterName     — defaults to "your team"; used in the email
 *   defaultAdminEmail — pre-fill the admin email field
 *   onSkip          — optional, only used in wizard context for "Set up later"
 *   onChange        — fires whenever the connection state changes (status update etc.)
 *   compact         — render in compact form for embedding inside another page
 *
 * Listens for `postMessage` from the OAuth callback popup so the wizard
 * advances automatically once the admin clicks Approve.
 */
export default function SharePointConnectCard({
  orgId,
  orgName,
  inviterName,
  defaultAdminEmail,
  onSkip,
  onChange,
  compact = false,
}) {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [adminEmail, setAdminEmail] = useState(defaultAdminEmail || '');
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [popup, setPopup] = useState(null);
  const pollIdRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setConnection(data.connection || null);
        if (onChange) onChange(data.connection || null);
      }
    } catch (e) {
      // Network errors are silent on background refresh; first-load failure is handled below.
    } finally {
      setLoading(false);
    }
  }, [orgId, onChange]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while a connect-popup is open OR status is in a transient state.
  useEffect(() => {
    const transient = connection && ['invited', 'consented', 'folder_selected'].includes(connection.status);
    const needPoll = !!popup || transient;
    if (!needPoll) {
      if (pollIdRef.current) { clearInterval(pollIdRef.current); pollIdRef.current = null; }
      return;
    }
    if (pollIdRef.current) return;
    pollIdRef.current = setInterval(refresh, 3000);
    return () => {
      if (pollIdRef.current) { clearInterval(pollIdRef.current); pollIdRef.current = null; }
    };
  }, [popup, connection, refresh]);

  // Listen for the OAuth callback popup's postMessage.
  useEffect(() => {
    function handler(ev) {
      if (!ev?.data || ev.data.source !== 'sharepoint-intake') return;
      if (ev.data.status === 'success') {
        if (popup) try { popup.close(); } catch {}
        setPopup(null);
        refresh();
        setShowFolderPicker(true);
      } else {
        if (popup) try { popup.close(); } catch {}
        setPopup(null);
        setError(ev.data.message || 'Microsoft did not approve the request');
        refresh();
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [popup, refresh]);

  const sendInvite = useCallback(async (resend = false) => {
    setError(null);
    const cleanEmail = (adminEmail || '').trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Enter the M365 admin's email address");
      return;
    }
    setBusy(true);
    try {
      const path = resend ? 'resend-invite' : 'invite';
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_email: cleanEmail, inviter_name: inviterName || null }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Could not send invite');
      } else if (!data.invite_email_sent) {
        setError(`Connection created but the email could not be sent: ${data.invite_email_error || 'unknown'}`);
      } else {
        await refresh();
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setBusy(false);
    }
  }, [adminEmail, inviterName, orgId, refresh]);

  const disconnect = useCallback(async () => {
    if (!connection?.id) return;
    if (!window.confirm('Disconnect SharePoint? PSI will stop pulling files immediately. You can reconnect anytime.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.success) setError(data.message || 'Could not disconnect');
      await refresh();
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setBusy(false);
    }
  }, [connection, orgId, refresh]);

  const togglePause = useCallback(async () => {
    if (!connection?.id) return;
    const action = connection.status === 'paused' ? 'resume' : 'pause';
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) setError(data.message || `Could not ${action}`);
      await refresh();
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setBusy(false);
    }
  }, [connection, orgId, refresh]);

  // ----- Rendering -----
  if (loading) {
    return (
      <div className={cardClass(compact)}>
        <p className="text-slate-400 text-sm">Loading SharePoint connection…</p>
      </div>
    );
  }

  const status = connection?.status || 'not_connected';

  return (
    <div className={cardClass(compact)}>
      <Header status={status} compact={compact} />

      {!connection || status === 'revoked' ? (
        <StateNoConnection
          orgName={orgName}
          adminEmail={adminEmail}
          setAdminEmail={setAdminEmail}
          onSend={() => sendInvite(false)}
          onSkip={onSkip}
          busy={busy}
          error={error}
        />
      ) : status === 'invited' ? (
        <StateInvited
          connection={connection}
          adminEmail={adminEmail}
          setAdminEmail={setAdminEmail}
          onResend={() => sendInvite(true)}
          busy={busy}
          error={error}
        />
      ) : status === 'consented' ? (
        <StateConsented
          onPickFolder={() => setShowFolderPicker(true)}
          adminEmail={connection.consented_by_email}
          error={error}
        />
      ) : status === 'active' || status === 'folder_selected' || status === 'paused' ? (
        <StateActive
          connection={connection}
          onChangeFolder={() => setShowFolderPicker(true)}
          onTogglePause={togglePause}
          onDisconnect={disconnect}
          busy={busy}
          error={error}
        />
      ) : status === 'error' ? (
        <StateError
          connection={connection}
          onReinvite={() => sendInvite(true)}
          onDisconnect={disconnect}
          busy={busy}
        />
      ) : null}

      {showFolderPicker && (
        <FolderPickerModal
          orgId={orgId}
          onClose={() => setShowFolderPicker(false)}
          onSelected={async () => {
            setShowFolderPicker(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function cardClass(compact) {
  return compact
    ? 'rounded-2xl border border-slate-700/60 bg-slate-900/40 p-5'
    : 'rounded-2xl border border-slate-700/60 bg-slate-900/60 p-6 shadow-xl shadow-black/20';
}

function Header({ status, compact }) {
  return (
    <div className="flex items-start justify-between mb-4 gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/30 to-amber-500/30 border border-orange-500/40 flex items-center justify-center flex-shrink-0">
          <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
            <rect x="1" y="1" width="10" height="10" fill="#F25022" />
            <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
            <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
            <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
          </svg>
        </div>
        <div>
          <h3 className={`font-semibold text-white ${compact ? 'text-sm' : 'text-base'}`}>SharePoint Intake</h3>
          <p className="text-xs text-slate-500">Documents flow from one chosen folder, automatically.</p>
        </div>
      </div>
      <StatusPill status={status} />
    </div>
  );
}

function StateNoConnection({ orgName, adminEmail, setAdminEmail, onSend, onSkip, busy, error }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
        <p className="text-sm text-slate-300 leading-relaxed">
          We&apos;ll email your <strong className="text-white">Microsoft 365 admin</strong>. They click <strong className="text-white">Approve</strong>, pick one folder, and {orgName || 'your team'} can drop files there from now on. <span className="text-slate-500">No drive mapping, no agent install, no VPN.</span>
        </p>
        <ul className="mt-3 space-y-1.5 text-xs text-slate-400">
          <li>• Read-only access to <strong className="text-white">one</strong> folder you pick</li>
          <li>• Logged in your M365 audit log <em>and</em> ours</li>
          <li>• Revoke anytime — one click</li>
        </ul>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">
          M365 admin&apos;s email
        </label>
        <div className="flex gap-2">
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="admin@clientco.com"
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition text-sm"
          />
          <button
            type="button"
            onClick={onSend}
            disabled={busy}
            className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition whitespace-nowrap"
          >
            {busy ? 'Sending…' : 'Send authorization email'}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          The link in the email expires in 15 minutes. You can resend anytime.
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      {onSkip && (
        <div className="pt-2 border-t border-slate-800">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2"
          >
            Skip — set this up later
          </button>
          <p className="text-[11px] text-slate-600 mt-1">
            You can connect SharePoint anytime from <span className="font-mono">Admin → SharePoint Intake</span>.
          </p>
        </div>
      )}
    </div>
  );
}

function StateInvited({ connection, adminEmail, setAdminEmail, onResend, busy, error }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <p className="text-sm text-amber-100">
          We emailed <strong className="text-white">{connection.invited_admin_email}</strong>. They have <strong>15 minutes</strong> to click the button in their email. We&apos;ll update this page automatically when they do.
        </p>
        {connection.invite_resent_count > 0 && (
          <p className="text-[11px] text-amber-200/70 mt-2">
            Resent {connection.invite_resent_count} time{connection.invite_resent_count === 1 ? '' : 's'} · last at {fmtDate(connection.invite_last_resent_at)}
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">
            Resend to a different email
          </label>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder={connection.invited_admin_email}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition text-sm"
          />
        </div>
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap"
        >
          {busy ? 'Sending…' : 'Resend invite'}
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function StateConsented({ onPickFolder, adminEmail, error }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4">
        <p className="text-sm text-sky-100">
          <strong className="text-white">{adminEmail || 'The M365 admin'}</strong> approved access. Now pick the one folder PSI should watch.
        </p>
      </div>
      <button
        type="button"
        onClick={onPickFolder}
        className="w-full px-5 py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white hover:shadow-lg hover:shadow-blue-500/25 transition"
      >
        Pick a SharePoint folder →
      </button>
      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function StateActive({ connection, onChangeFolder, onTogglePause, onDisconnect, busy, error }) {
  const paused = connection.status === 'paused';
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <div className="text-emerald-300/70 text-xs uppercase tracking-wide">Site</div>
          <div className="text-white truncate">{connection.sharepoint_site_name || '—'}</div>
          <div className="text-emerald-300/70 text-xs uppercase tracking-wide">Folder</div>
          <div className="text-white truncate font-mono text-[13px]">{connection.sharepoint_folder_path || '—'}</div>
          <div className="text-emerald-300/70 text-xs uppercase tracking-wide">Files pulled</div>
          <div className="text-white">{Number(connection.files_pulled_total || 0).toLocaleString()}</div>
          <div className="text-emerald-300/70 text-xs uppercase tracking-wide">Last sync</div>
          <div className="text-white">{fmtDate(connection.last_synced_at) || 'Never (waiting for first file)'}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onChangeFolder}
          disabled={busy}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-200 transition disabled:opacity-50"
        >
          Change folder
        </button>
        <button
          type="button"
          onClick={onTogglePause}
          disabled={busy}
          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-200 transition disabled:opacity-50"
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={busy}
          className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-xs text-red-300 transition disabled:opacity-50 ml-auto"
        >
          Disconnect
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
    </div>
  );
}

function StateError({ connection, onReinvite, onDisconnect, busy }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
        <p className="text-sm text-red-100 font-medium">Something went wrong with the connection.</p>
        {connection.error_message && (
          <p className="text-[12px] text-red-200/80 mt-1 font-mono break-words">{connection.error_message}</p>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onReinvite}
          disabled={busy}
          className="px-3 py-1.5 bg-blue-500/20 border border-blue-500/40 rounded-lg text-xs text-blue-200 hover:bg-blue-500/30 disabled:opacity-50 transition"
        >
          Re-invite admin
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={busy}
          className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-50 transition"
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">
      {message}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
}

// ---------------------------------------------------------------------------
// Folder picker modal
// ---------------------------------------------------------------------------

function FolderPickerModal({ orgId, onClose, onSelected }) {
  const [step, setStep] = useState('sites'); // sites | drives | folders
  const [sites, setSites] = useState([]);
  const [search, setSearch] = useState('');
  const [drives, setDrives] = useState([]);
  const [folders, setFolders] = useState([]);
  const [path, setPath] = useState([]); // breadcrumbs: [{kind:'site',id,name},{kind:'drive',id,name},{kind:'folder',id,name}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSites = useCallback(async (s) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/folders/sites`, window.location.origin);
      if (s) url.searchParams.set('search', s);
      const res = await fetch(url.pathname + url.search, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setSites(data.sites || []);
      else setError(data.message || 'Failed to load sites');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }, [orgId]);

  const loadDrives = useCallback(async (siteId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/folders/drives?site_id=${encodeURIComponent(siteId)}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDrives(data.drives || []);
      else setError(data.message || 'Failed to load drives');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }, [orgId]);

  const loadFolders = useCallback(async (driveId, folderId) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/folders/children`, window.location.origin);
      url.searchParams.set('drive_id', driveId);
      if (folderId) url.searchParams.set('folder_id', folderId);
      const res = await fetch(url.pathname + url.search, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setFolders(data.folders || []);
      else setError(data.message || 'Failed to load folders');
    } catch (e) { setError('Network error: ' + e.message); }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { loadSites(''); }, [loadSites]);

  const pickSite = async (site) => {
    setPath([{ kind: 'site', id: site.id, name: site.name, web_url: site.web_url }]);
    setStep('drives');
    await loadDrives(site.id);
  };

  const pickDrive = async (drive) => {
    setPath((p) => [...p, { kind: 'drive', id: drive.id, name: drive.name }]);
    setStep('folders');
    await loadFolders(drive.id, null);
  };

  const pickFolder = async (folder) => {
    setPath((p) => [...p, { kind: 'folder', id: folder.id, name: folder.name }]);
    await loadFolders(path.find((x) => x.kind === 'drive').id, folder.id);
  };

  const goUp = async () => {
    const newPath = path.slice(0, -1);
    setPath(newPath);
    const last = newPath[newPath.length - 1];
    if (!last) { setStep('sites'); await loadSites(search); }
    else if (last.kind === 'site') { setStep('drives'); await loadDrives(last.id); }
    else if (last.kind === 'drive') { setStep('folders'); await loadFolders(last.id, null); }
    else if (last.kind === 'folder') {
      const driveCrumb = newPath.find((x) => x.kind === 'drive');
      await loadFolders(driveCrumb.id, last.id);
    }
  };

  const selectCurrent = async () => {
    const site = path.find((x) => x.kind === 'site');
    const drive = path.find((x) => x.kind === 'drive');
    const folderCrumbs = path.filter((x) => x.kind === 'folder');
    const folder = folderCrumbs[folderCrumbs.length - 1];
    if (!site || !drive || !folder) {
      setError('Drill into a folder first');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/folder`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: site.id,
          site_url: site.web_url,
          site_name: site.name,
          drive_id: drive.id,
          folder_id: folder.id,
          folder_path: folderCrumbs.map((c) => c.name).join(' / '),
        }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Could not save folder selection'); setLoading(false); return; }
      onSelected();
    } catch (e) {
      setError('Network error: ' + e.message);
      setLoading(false);
    }
  };

  const breadcrumb = path.map((p, i) => p.name).join(' / ');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-white">Pick a SharePoint folder</h3>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate">{breadcrumb || 'Choose a site to start'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {step === 'sites' && (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadSites(search); }}
                placeholder="Search sites by name…"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition text-sm mb-3"
              />
              {loading ? <Loader /> : <List items={sites} onPick={pickSite} kind="site" />}
            </>
          )}
          {step === 'drives' && (loading ? <Loader /> : <List items={drives} onPick={pickDrive} kind="drive" />)}
          {step === 'folders' && (loading ? <Loader /> : <List items={folders} onPick={pickFolder} kind="folder" emptyMessage="No subfolders here. You can select this folder using the button below." />)}

          {error && <div className="mt-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between flex-shrink-0 gap-2">
          <div className="flex gap-2">
            {path.length > 0 && (
              <button onClick={goUp} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs text-slate-200 transition">← Back</button>
            )}
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-400 hover:text-white transition">Cancel</button>
          </div>
          <button
            onClick={selectCurrent}
            disabled={!path.some((p) => p.kind === 'folder') || loading}
            className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition"
          >
            Use this folder
          </button>
        </div>
      </div>
    </div>
  );
}

function List({ items, onPick, kind, emptyMessage }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-500 italic">{emptyMessage || `No ${kind}s found.`}</p>;
  }
  return (
    <div className="space-y-1">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onPick(item)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-700/60 bg-slate-800/40 hover:bg-slate-800 hover:border-slate-600 transition text-left"
        >
          <Icon kind={kind} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate">{item.name}</div>
            {item.web_url && <div className="text-[11px] text-slate-500 truncate">{item.web_url}</div>}
            {item.drive_type && <div className="text-[11px] text-slate-500">{item.drive_type}</div>}
          </div>
          <span className="text-slate-500 text-sm">›</span>
        </button>
      ))}
    </div>
  );
}

function Icon({ kind }) {
  const cls = 'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm';
  if (kind === 'site') return <div className={`${cls} bg-blue-500/30`}>S</div>;
  if (kind === 'drive') return <div className={`${cls} bg-amber-500/30`}>D</div>;
  return <div className={`${cls} bg-emerald-500/30`}>📁</div>;
}

function Loader() {
  return (
    <div className="py-8 text-center">
      <div className="w-8 h-8 mx-auto border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
